/**
 * Step 1 of the Add Manually wizard: the procedures to ask, filled in place.
 *
 * Each column is one group of a line's fields. In the open row one group is
 * open at a time, left to right: its column widens for every row (so the
 * columns stay aligned) and ✓ — or Enter — closes it and opens the next. After
 * the last group the row turns green and the next unfinished row opens. A
 * closed cell says what it holds; clicking it opens it again.
 *
 * State lives in the wizard (rows, cursor, ortho terms); this draws it and
 * reports changes through `onChange(rows, cursor)`.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, DatePicker, Input, Select, Tooltip, message } from "antd";
import { CheckOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useDebounce } from "use-debounce";

import { controller } from "../../../controller";
import { LINE_CHOICES, choiceLabel, statusFromPercent } from "../../../lib/planDetails";
import { SECTION_ORDER } from "../sheet/sheetModel";
import {
  applySameRule, confirmGroup, entryRow, firstOpenGroup, groupSummary, groupsFor, isOrthoCode, mirrorSharedLimit, nameOf,
  nextEligibleFrom, orthoPaymentOptions, removeRow, reopen, rowError, sameRuleTargets, setField, skipRow,
} from "./entryModel";
import "./manualWizard.css";

const CATEGORY_OPTIONS = SECTION_ORDER.map((c) => ({ value: c, label: c }));

// Column widths (%): the open group takes the room it needs, the closed share the rest.
const OPEN_WIDTH = { covered: 28, often: 34, ages: 15, ortho: 22, shared: 28, history: 26, note: 28 };

function sectionOf(row) {
  return SECTION_ORDER.indexOf(row.category) === -1 ? "Added" : row.category;
}

export default function ProcedureEntryTable({
  rows, cursor, terms, pmsLastDone, dismissed, onChange, onTerms, onDismiss,
}) {
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [debounced] = useDebounce(search, 350);
  const [options, setOptions] = useState([]);

  useEffect(() => { setError(null); }, [cursor && cursor.row, cursor && cursor.group]);

  useEffect(() => {
    if (!debounced) { setOptions([]); return undefined; }
    let live = true;
    const digits = (debounced.match(/\d+/g) || []).join("");
    const words = debounced.replace(/\d+/g, "").replace(/^-+|-+$/g, "").trim();
    controller.getProcedure(0, { procedureCode: digits, procedureDescription: words }).then((res) => {
      if (!live || !res || res.status >= 250) return;
      const have = new Set(rows.map((r) => r.code));
      setOptions((res.json || []).filter((p) => !have.has(String(p.procedure_code))).map((p) => ({
        value: String(p.procedure_code),
        label: `${p.procedure_code}${p.procedure_code_description ? ` - ${p.procedure_code_description}` : ""}`,
        name: p.procedure_code_description || "",
      })));
    }).catch(() => {});
    return () => { live = false; };
  }, [debounced, rows]);

  const hasOrtho = rows.some((r) => isOrthoCode(r.code));
  const columns = useMemo(() => groupsFor(hasOrtho ? "80000" : "01202"), [hasOrtho]);
  const openGroup = cursor ? cursor.group : null;
  const widths = useMemo(() => {
    const procedure = 16;
    const actions = 5;
    const open = openGroup && OPEN_WIDTH[openGroup] ? OPEN_WIDTH[openGroup] : 0;
    const closed = columns.length - (open ? 1 : 0);
    const each = (100 - procedure - actions - open) / closed;
    return { procedure, actions, of: (key) => (key === openGroup ? open : each) };
  }, [columns, openGroup]);

  const sections = useMemo(() => {
    const out = [];
    rows.forEach((row, index) => {
      const name = sectionOf(row);
      let sec = out.find((s) => s.name === name);
      if (!sec) { sec = { name, items: [] }; out.push(sec); }
      sec.items.push({ row, index });
    });
    const order = SECTION_ORDER.concat("Added");
    return out.sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  }, [rows]);

  // ---- actions ----
  const edit = (index, field) => (v) => {
    const value = v && v.target ? v.target.value : v;
    onChange(setField(rows, index, field, value), cursor);
  };
  function confirm() {
    if (!cursor) return;
    const problem = rowError(rows[cursor.row]);
    if (problem) { setError(problem); return; }
    let next = confirmGroup(rows, cursor);
    if (cursor.group === "shared") {
      const m = mirrorSharedLimit(next.rows, cursor.row);
      if (m.mirrored.length) {
        next = { rows: m.rows, cursor: next.cursor };
        message.info(`Shared limit filled on ${m.mirrored.join(" and ")} too`);
      }
    }
    onChange(next.rows, next.cursor);
  }
  const open = (index, group) => { const next = reopen(rows, index, group); onChange(next.rows, next.cursor); };
  const skip = (index) => { const next = skipRow(rows, index); onChange(next.rows, next.cursor); };
  const remove = (index) => {
    const out = removeRow(rows, index);
    let c = cursor;
    if (c && c.row === index) c = null;
    else if (c && c.row > index) c = { row: c.row - 1, group: c.group };
    onChange(out, c);
  };
  function addCode(code, option) {
    const out = rows.concat(entryRow({ code, name: (option && option.name) || "", category: "" }));
    onChange(out, { row: out.length - 1, group: "covered" });
    setSearch("");
    setOptions([]);
  }
  const onKey = (e) => {
    if (e.key !== "Enter" || e.shiftKey) return;
    const t = e.target;
    if (t && (t.tagName === "TEXTAREA" || (t.closest && t.closest(".ant-select, .ant-picker")))) return;
    e.preventDefault();
    confirm();
  };

  // ---- the open group's fields ----
  function editor(row, index, group) {
    const e = row.edit;
    const id = (f) => `mw-${f}`;
    switch (group) {
      case "covered": {
        const implied = e.coverage_status ? "" : statusFromPercent(e.percentage_covered, "");
        return (
          <div className="mw-fields">
            <Input id={id("pct")} autoFocus aria-label="Coverage %" placeholder="%" suffix="%" className="mw-w-pct"
              value={e.percentage_covered} onChange={edit(index, "percentage_covered")} />
            <Select id={id("status")} aria-label="Status" allowClear className="mw-w-select" options={LINE_CHOICES.coverage_status}
              placeholder={implied ? `${choiceLabel(LINE_CHOICES.coverage_status, implied)} (from the %)` : "Status"}
              value={e.coverage_status || undefined} onChange={(v) => edit(index, "coverage_status")(v || "")} />
            <Select aria-label="Category" allowClear className="mw-w-cat" options={CATEGORY_OPTIONS} placeholder="Category"
              value={e.coverage_type || undefined} onChange={(v) => edit(index, "coverage_type")(v || "")} />
            <Select aria-label="Alternate benefit" allowClear className="mw-w-select" options={LINE_CHOICES.alternate_benefit}
              placeholder="Alternate benefit" value={e.alternate_benefit || undefined}
              onChange={(v) => edit(index, "alternate_benefit")(v || "")} />
          </div>
        );
      }
      case "often":
        return (
          <div className="mw-fields">
            <Input id={id("count")} autoFocus aria-label="Occurrences" placeholder="Count" className="mw-w-num" disabled={e.isFrequencyUnlimited}
              value={e.isFrequencyUnlimited ? "" : e.max_occurrences} onChange={edit(index, "max_occurrences")} />
            <span className="mw-word">every</span>
            <Input id={id("months")} aria-label="Months" placeholder="Months" className="mw-w-num" disabled={e.isFrequencyUnlimited}
              value={e.isFrequencyUnlimited ? "" : e.coverage_duration_months} onChange={edit(index, "coverage_duration_months")} />
            <span className="mw-word">months</span>
            <Checkbox checked={e.isFrequencyUnlimited} onChange={(ev) => edit(index, "isFrequencyUnlimited")(ev.target.checked)}>No limit</Checkbox>
            <Select aria-label="Basis" allowClear className="mw-w-select" options={LINE_CHOICES.frequency_basis} placeholder="Rolling or calendar?"
              value={e.frequency_basis || undefined} onChange={(v) => edit(index, "frequency_basis")(v || "")} />
            <Select aria-label="Counts" allowClear className="mw-w-small" options={LINE_CHOICES.limit_unit} placeholder="Counts"
              value={e.limit_unit || undefined} onChange={(v) => edit(index, "limit_unit")(v || "")} />
            <Select aria-label="Limited per" allowClear className="mw-w-small" options={LINE_CHOICES.limit_scope} placeholder="Limited per"
              value={e.limit_scope || undefined} onChange={(v) => edit(index, "limit_scope")(v || "")} />
          </div>
        );
      case "ages":
        return (
          <div className="mw-fields">
            <Input id={id("age-min")} autoFocus aria-label="From age" placeholder="From" className="mw-w-num" value={e.age_min} onChange={edit(index, "age_min")} />
            <span className="mw-word">to</span>
            <Input id={id("age-max")} aria-label="To age" placeholder="To" className="mw-w-num" value={e.age_max} onChange={edit(index, "age_max")} />
          </div>
        );
      case "ortho":
        return (
          <div className="mw-fields">
            <Select aria-label="Ortho payment" allowClear autoFocus className="mw-w-select" options={orthoPaymentOptions()} placeholder="How it's paid"
              value={terms.ortho_payment || undefined}
              onChange={(v) => onTerms(Object.assign({}, terms, { ortho_payment: v || "" }))} />
            <Input aria-label="Paid on the initial fee" placeholder="On the initial fee" suffix="%" className="mw-w-pct2"
              value={terms.ortho_initial_payment_pct}
              onChange={(ev) => onTerms(Object.assign({}, terms, { ortho_initial_payment_pct: ev.target.value }))} />
            <span className="mw-hint">The plan's terms, the same for every ortho code.</span>
          </div>
        );
      case "shared":
        return (
          <div className="mw-fields">
            <Input id={id("shared-codes")} autoFocus aria-label="Shares its limit with" placeholder="Codes, e.g. 01204, 01205" className="mw-w-codes"
              value={e.shared_limit_codes_text} onChange={edit(index, "shared_limit_codes_text")} />
            <Input aria-label="Combined count" placeholder="#" className="mw-w-num" value={e.shared_limit_count} onChange={edit(index, "shared_limit_count")} />
            <span className="mw-word">every</span>
            <Input aria-label="Combined months" placeholder="Months" className="mw-w-num" value={e.shared_limit_months} onChange={edit(index, "shared_limit_months")} />
            <span className="mw-word">months, combined</span>
          </div>
        );
      case "history": {
        const pms = (pmsLastDone || {})[row.code];
        const next = nextEligibleFrom(e);
        return (
          <div className="mw-fields">
            <DatePicker id={id("last")} autoFocus aria-label="Last done" placeholder="Last done" format="YYYY-MM-DD" className="mw-w-date"
              value={e.last_service_date ? dayjs(e.last_service_date, "YYYY-MM-DD") : null}
              onChange={(d) => edit(index, "last_service_date")(d ? d.format("YYYY-MM-DD") : "")} />
            <DatePicker aria-label="Next eligible" placeholder="Next eligible" format="YYYY-MM-DD" className="mw-w-date"
              value={e.eligible_again_on ? dayjs(e.eligible_again_on, "YYYY-MM-DD") : null}
              onChange={(d) => edit(index, "eligible_again_on")(d ? d.format("YYYY-MM-DD") : "")} />
            {pms && pms !== e.last_service_date ? (
              <span className="mw-sugg">
                {`This office's PMS: done ${pms}`}
                <button type="button" className="bs-link" onClick={() => edit(index, "last_service_date")(pms)}>Use</button>
              </span>
            ) : null}
            {next && next !== e.eligible_again_on ? (
              <span className="mw-sugg" title="Once every N months, rolling: the last date plus N months">
                {`Next eligible ${next}`}
                <button type="button" className="bs-link" onClick={() => edit(index, "eligible_again_on")(next)}>Use</button>
              </span>
            ) : null}
          </div>
        );
      }
      case "note":
        return (
          <div className="mw-fields">
            <Input.TextArea autoFocus aria-label="Note" autoSize={{ minRows: 1, maxRows: 5 }} className="mw-w-note"
              placeholder="What the rep or the portal said, word for word" value={e.notes} onChange={edit(index, "notes")} />
          </div>
        );
      default:
        return null;
    }
  }

  function cell(row, index, group) {
    if (group.ortho && !isOrthoCode(row.code)) return <td key={group.key} className="mw-cell na" />;
    const isOpen = cursor && cursor.row === index && cursor.group === group.key;
    if (isOpen) {
      return (
        <td key={group.key} className="mw-cell open" onKeyDown={onKey}>
          {editor(row, index, group.key)}
          <div className="mw-open-foot">
            {error ? <span className="mw-error" role="alert">{error}</span> : <span className="mw-keys">Enter to confirm</span>}
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={confirm} aria-label={`Done with ${group.title}`}>Done</Button>
          </div>
        </td>
      );
    }
    const summary = row.skipped ? "" : groupSummary(row, group.key, terms);
    const done = !!row.done[group.key];
    return (
      <td key={group.key} className={`mw-cell${done ? " done" : ""}${summary === "—" ? " empty" : ""}`}>
        <button type="button" className="mw-cell-btn" title={summary && summary !== "—" ? summary : `Fill in ${group.title.toLowerCase()}`}
          onClick={() => open(index, group.key)}>
          <span className="mw-sum">{summary}</span>
          {group.key === "shared" && row.mirroredFrom ? <span className="mw-from">{`from ${row.mirroredFrom}`}</span> : null}
        </button>
      </td>
    );
  }

  return (
    <div className="mw-entry bs-fields">
      <table className="mw-table">
        <colgroup>
          <col style={{ width: `${widths.procedure}%` }} />
          {columns.map((g) => <col key={g.key} style={{ width: `${widths.of(g.key)}%` }} />)}
          <col style={{ width: `${widths.actions}%` }} />
        </colgroup>
        <thead>
          <tr>
            <th>Procedure</th>
            {columns.map((g) => <th key={g.key} className={g.key === openGroup ? "open" : undefined}>{g.title}</th>)}
            <th aria-label="Row actions" />
          </tr>
        </thead>
        <tbody>
          {sections.map((sec) => (
            <React.Fragment key={sec.name}>
              <tr className="mw-sec"><td colSpan={columns.length + 2}>{sec.name}</td></tr>
              {sec.items.map(({ row, index }) => {
                const n = nameOf(row);
                const active = cursor && cursor.row === index;
                const state = row.skipped ? "skipped" : row.complete ? "complete" : active ? "active" : "";
                const targets = row.complete && !row.skipped && dismissed.indexOf(row.code) === -1 ? sameRuleTargets(rows, index) : [];
                return (
                  <React.Fragment key={row.code}>
                    <tr className={`mw-row ${state}`} data-code={row.code}>
                      <td className="mw-proc">
                        <span className="mw-mark" aria-hidden="true">{row.complete && !row.skipped ? <CheckOutlined /> : null}</span>
                        <div className="mw-proc-text">
                          <button type="button" className="mw-name" onClick={() => open(index, firstOpenGroup(row) || "covered")}>
                            {n.name}{n.qualifier ? <span className="mw-q">{`, ${n.qualifier}`}</span> : null}
                          </button>
                          <span className="mw-code">{row.code}</span>
                          {row.skipped ? <div className="mw-note">Skipped: saved empty, still to ask</div>
                            : row.filledBy ? <div className="mw-note">{row.filledBy}</div> : null}
                        </div>
                      </td>
                      {columns.map((g) => cell(row, index, g))}
                      <td className="mw-acts">
                        {!row.skipped ? (
                          <Tooltip title="Save it empty (still to ask)">
                            <button type="button" className="bs-link mw-skip" onClick={() => skip(index)}>Skip</button>
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Leave it off this breakdown">
                          <button type="button" className="mw-del" aria-label={`Remove ${row.code}`} onClick={() => remove(index)}><DeleteOutlined /></button>
                        </Tooltip>
                      </td>
                    </tr>
                    {targets.length ? (
                      <tr className="mw-offer">
                        <td colSpan={columns.length + 2}>
                          <span>{`${targets.map((i) => rows[i].code).join(" and ")} ${targets.length === 1 ? "is" : "are"} usually answered with ${row.code}'s rule.`}</span>
                          <Button size="small" onClick={() => onChange(applySameRule(rows, index, targets), cursor)}>
                            {`Same rule for ${targets.length === 1 ? "it" : "them"}`}
                          </Button>
                          <button type="button" className="bs-link" onClick={() => onDismiss(row.code)}>Answer them separately</button>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div className="mw-add">
        <Select showSearch filterOption={false} value={null} placeholder="Add a procedure: search a code or a name"
          onSearch={setSearch} searchValue={search} options={options} onChange={addCode} notFoundContent={debounced ? "No match in this office's codes" : null}
          className="mw-add-select" aria-label="Add a procedure" />
      </div>
    </div>
  );
}
